import hidePasswordIcon from '_assets/img/icons/svg/hide.svg';
import showPasswordIcon from '_assets/img/icons/svg/show.svg';
import { TranslatableString } from '_i18n/no/translations';
import UADetector from 'Helper/UADetector';
import { ChangeEvent, FC, FocusEvent, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './InputBox.scss';

type InputTypeShape = 'text' | 'number' | 'password' | 'email';

type PropsType = {
    classes?: string;
    size?: 'small' | 'large';
    type?: InputTypeShape;
    label?:
        | TranslatableString
        | {
              label: TranslatableString;
              params: { [key: string]: string };
          };
    value?: string | number;
    min?: number;
    step?: number | string;
    id: string;
    handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleFocus?: (event: FocusEvent<HTMLInputElement>) => void;
    handleBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: TranslatableString;

    canTogglePasswordReadability?: boolean;
    /** keeps height of box constant with or without error */
    heightIncludesError?: boolean;
    error?: string;
    readonly?: boolean;
    autoComplete?: 'on' | 'off';
    testId?: string;

    inputMode?:
        | 'search'
        | 'text'
        | 'email'
        | 'tel'
        | 'url'
        | 'none'
        | 'numeric'
        | 'decimal';
    autoFocus?: boolean;
};
const InputBox: FC<PropsType> = ({
    classes,
    size,
    type = 'text',
    label,
    value,
    handleChange,
    handleFocus,
    handleBlur,
    onKeyDown,
    placeholder,
    canTogglePasswordReadability,
    heightIncludesError = false,
    error,
    readonly = false,
    autoComplete = 'off',
    testId,
    autoFocus = false,
    ...otherInputProps
}) => {
    const { t } = useTranslation();
    const [showText, setShowText] = useState(false);
    const device = UADetector();
    const suffixIconClasses = `suffix-icon ${
        device.isIPad ? 'suffix-icon-ipad' : ''
    }`;

    const inputBoxClasses = `input-box ${
        device.isIPad ? 'input-box-ipad' : ''
    }`;
    const getInputType = (type: InputTypeShape): InputTypeShape => {
        /**
         * If type is password and showText = true
         * then we set type to text to show the input
         * text value
         */
        if (type === 'password' && showText) {
            return 'text';
        }

        /**
         * If type is password and showText = false
         * then we set type to password to hide input
         * text value
         */
        if (type === 'password' && !showText) {
            return 'password';
        }

        /**
         * Otherwise set the type as we
         * get as props
         */
        return type;
    };

    const labelText = label
        ? typeof label === 'string'
            ? t(label)
            : // TODO: fix this
              t(label.label, label.params as unknown as string)
        : '';

    return (
        <div
            className={`${inputBoxClasses} ${classes ? classes : ''} ${
                size ? `input-box-${size}` : ''
            }`}
        >
            {label && <div className="input-label">{labelText}</div>}
            <div
                data-cy="input-value"
                className={`input-value ${error ? 'input-error' : ''}`}
            >
                <input
                    className="visible-input"
                    type={getInputType(type)}
                    value={value ?? ''}
                    onChange={handleChange}
                    placeholder={
                        placeholder ? t(placeholder) : (labelText ?? '')
                    }
                    readOnly={readonly}
                    autoComplete={autoComplete}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={onKeyDown}
                    data-cy={testId}
                    autoFocus={autoFocus}
                    {...otherInputProps}
                />
                {type === 'password' && canTogglePasswordReadability && (
                    <div className="suffix-icon-wrapper">
                        <div
                            className={`${suffixIconClasses} ${
                                !showText ? 'text-hide' : ''
                            }`}
                        >
                            <img
                                src={
                                    showText
                                        ? showPasswordIcon
                                        : hidePasswordIcon
                                }
                                alt="hide icon"
                                onClick={() => setShowText((status) => !status)}
                            />
                        </div>
                    </div>
                )}
            </div>
            {(error || heightIncludesError) && (
                <div className={`error-msg ${error ? 'has-error' : ''}`}>
                    {error || '\u00A0'}
                </div>
            )}
        </div>
    );
};

export default InputBox;
